import * as React from 'react';
import { toast } from 'sonner';
import { useTranslation } from '../../../hooks/useTranslation';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import type { OrgArtifactViewDefinitionLike } from '../models/orgArtifactKind';
import { openAtlasChanges } from '../stores/atlasChangesStore';
import { useCommitScenario } from './useCommitScenario';
import { atlasPlanningErrorInput, presentAtlasPlanningError } from '../services/atlasPlanningErrorPresentation';
import { useAtlasDraftSaveStatus } from './useAtlasDraftSaveStatus';

export function shouldSettlePreparedClientScenario(input: {
  readonly hasScenario: boolean;
  readonly legacyChangeCount: number;
  readonly unsupportedCount: number;
}): boolean {
  return input.hasScenario && input.legacyChangeCount === 0 && input.unsupportedCount === 0;
}

/**
 * The "Apply changes" action: confirm with blast radius, apply the active
 * scenario's changes to the canonical org model, and report the outcome
 * (including any change types that cannot be applied yet).
 *
 * Shared so every surface that offers apply — the scenarios panel and the
 * active-scenario banner — behaves identically. "Apply" is the locked verb in
 * `registry/lexicon.md`; avoid "commit" (engineering jargon).
 */
export function useCommitScenarioAction(input: {
  folderId: string | null | undefined;
  diagramId: string | null | undefined;
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined;
}) {
  const { t } = useTranslation('diagram');
  const { commit, isCommitting } = useCommitScenario(input);
  const draftSave = useAtlasDraftSaveStatus(input.folderId ?? '', input.diagramId ?? '').state;
  const [applying, setApplying] = React.useState(false);
  const commitWithConfirm = React.useCallback(async () => {
    const scenarioAtApply = useScenariosStore.getState().getActiveScenario();
    setApplying(true);
    try {
      const result = await commit();
      if (!result || result.status === 'nothing_to_commit') {
        toast.message(
          t('scenarios.applyNothing', { defaultValue: 'Nothing to apply yet' }) as string,
          result?.unsupported.length
            ? { description: t('scenarios.applyUnsupportedOnly', { defaultValue: 'These kinds of changes can’t be applied to the org chart yet.' }) as string }
            : undefined,
        );
        return;
      }
      if (result.status === 'prepared' || result.status === 'staged') {
        if (result.status === 'prepared') {
          if (!result.operationalChangeSetId) {
            throw new Error('Operational change preparation did not return a review ID');
          }
          if (!input.folderId || !input.diagramId) {
            throw new Error('Operational change preparation lost its chart context');
          }
        }
        const linkedScenario = scenarioAtApply ? useScenariosStore.getState().getScenario(scenarioAtApply.id) : undefined;
        const alreadyLinked = linkedScenario?.submissionState === 'submitted'
          && linkedScenario.submittedChangeSetId === result.changeSetId
          && linkedScenario.submittedScenarioId === result.scenarioId;
        const completedPreparedDraft = alreadyLinked || (shouldSettlePreparedClientScenario({
          hasScenario: Boolean(scenarioAtApply),
          legacyChangeCount: scenarioAtApply?.changes.length ?? 0,
          unsupportedCount: result.unsupported.length,
        }) && scenarioAtApply
          && result.changeSetId
          && result.scenarioId
          ? result.status === 'staged'
            ? useScenariosStore.getState().markScenarioSubmitted(
                scenarioAtApply.id,
                scenarioAtApply.updatedAt.toISOString(),
                { scenarioId: result.scenarioId, changeSetId: result.changeSetId },
              )
            : useScenariosStore.getState().markScenarioSubmitted(
                scenarioAtApply.id,
                scenarioAtApply.updatedAt.toISOString(),
                {
                  scenarioId: result.scenarioId,
                  changeSetId: result.changeSetId,
                  operationalChangeSetId: result.operationalChangeSetId,
                },
              )
          : false);
        if (result.status === 'prepared') {
          openAtlasChanges(input.folderId!, input.diagramId!, result.operationalChangeSetId!);
        }
        if (result.status === 'staged' && result.changeSetId && input.folderId && input.diagramId) {
          openAtlasChanges(input.folderId, input.diagramId, result.changeSetId);
        }
        const extra = result.unsupported.length
          ? ` · ${result.unsupported.length} ${t('scenarios.applyNotYet', { defaultValue: 'couldn’t be prepared yet' })}`
          : scenarioAtApply && !completedPreparedDraft
            ? ` · ${t('scenarios.applyDraftRemains', { defaultValue: 'some draft changes remain' })}`
          : '';
        toast.success(result.status === 'staged'
          ? `${t('scenarios.dashboardProposalReady', { defaultValue: 'Plan saved — ready to review' })}${extra}`
          : `${t('scenarios.applyPrepared', { defaultValue: 'Organization change ready to review' })}${extra}`);
        return;
      }
      const completedAppliedDraft = result.unsupported.length === 0
        && scenarioAtApply
        && scenarioAtApply.changes.length === 0
        ? useScenariosStore.getState().completeAppliedScenario(
            scenarioAtApply.id,
            scenarioAtApply.updatedAt.toISOString(),
          )
        : false;
      const extra = result.unsupported.length
        ? ` · ${result.unsupported.length} ${t('scenarios.applyNotYet', { defaultValue: 'couldn’t be applied yet' })}`
        : scenarioAtApply && !completedAppliedDraft
          ? ` · ${t('scenarios.applyDraftRemains', { defaultValue: 'some draft changes remain' })}`
        : '';
      toast.success(`${t('scenarios.applyDone', { count: result.appliedCount, defaultValue: 'Applied {{count}} change(s) to the org chart' })}${extra}`);
    } catch (error) {
      const presentation = presentAtlasPlanningError(
        atlasPlanningErrorInput(error),
        { operation: 'apply', draftSaved: true },
      );
      toast.error(
        t('scenarios.applyFailed', { defaultValue: 'Could not apply changes' }) as string,
        { description: presentation.message },
      );
    } finally {
      setApplying(false);
    }
  }, [commit, input.diagramId, input.folderId, t]);

  return {
    commitWithConfirm,
    isCommitting,
    /** A canvas edit is still saving or its save is unresolved; the transfer would be refused. */
    isDraftSaveBlocking: draftSave !== 'idle' && !applying,
    isDraftSaving: (draftSave === 'saving' || draftSave === 'refreshing') && !applying,
    confirmationDialog: null,
  };
}
